import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { HttpClientCustom } from 'src/providers/custom/http-client';
import { Addresses } from 'src/providers/customer/addresses';
import { Me } from 'src/providers/me/me';
import { Events } from 'src/providers/events/events';

import { environment } from 'src/environments/environment';
import { REPORT_SLOW_REQUEST } from 'src/app/interceptors/error.interceptor';
import { CookieService } from 'ngx-cookie';
import { TidySession } from '../tidy-session/tidy-session';
import { privateProSignUpDataModel } from 'src/models/private-pro-signup';

@Injectable()
export class Auth {

  readonly authenticated = signal(
    localStorage.getItem('tidy_token') !== null && localStorage.getItem('tidy_token_type') !== null
  );
  credentials: any;
  private apiUrl: string = environment.api_secure;

  // While a concierge "login as client" token swap is underway, stray 401s from the OUTGOING
  // session (or in-flight bootstrap requests racing the swap) must NOT trigger the global
  // logout-on-401 — that wipes the freshly minted session and bounces the user to the auth
  // modal. app.component checks this before acting on `authToken:isInvalid`.
  private tokenLoginInProgress = false;
  private tokenLoginTimer: ReturnType<typeof setTimeout> | null = null;
  private trackedSessionKey: string | null = null;

  constructor(
    private httpClient: HttpClientCustom,
    private addresses: Addresses,
    private me: Me,
    private cookieService: CookieService,
    private events: Events,
    private tidySession: TidySession,
    private http: HttpClient
  ) {}

  cleanStorageAndPreserveUuid() {
    const uuid = localStorage.getItem('uuid');
    const themeMode = localStorage.getItem('tidy_theme_mode');
    const themeUserSet = localStorage.getItem('tidy_theme_user_set');
    localStorage.clear();
    if (uuid !== null) {
      localStorage.setItem('uuid', uuid);
    }
    if (themeMode !== null) {
      localStorage.setItem('tidy_theme_mode', themeMode);
    }
    if (themeUserSet !== null) {
      localStorage.setItem('tidy_theme_user_set', themeUserSet);
    }
    this.authenticated.set(false);
  }

  getTidyToken(): Promise<any> {
    const credentials = this.addCredentials({}, 'signUp');
    const url = 'api/v1/token';

    return this.httpClient.post(url, credentials);
  }

  async login(identity, type, privateProSignUpData: privateProSignUpDataModel = null): Promise<any> {
    const credentials = this.addCredentials(identity, type, privateProSignUpData?.hkId);
    const url = 'api/v1/token';
    const authResponse = await this.httpClient.post(url, credentials);
    localStorage.setItem('tidy_token', authResponse.access_token);
    localStorage.setItem('tidy_token_type', authResponse.token_type);
    if (identity?.username === 'demos@tidy.com') {
      localStorage.setItem('isDemoAccount', 'true');
    } else {
      localStorage.removeItem('isDemoAccount');
    }
    this.authenticated.set(true);
    document.cookie = 'tidy_logged_in=true; path=/; max-age=31536000';

    // Save all UTM parameters to localStorage
    if (identity.utm_source) {
      localStorage.setItem('utm_source', identity.utm_source);
    }
    if (identity.utm_medium) {
      localStorage.setItem('utm_medium', identity.utm_medium);
    }
    if (identity.utm_campaign) {
      localStorage.setItem('utm_campaign', identity.utm_campaign);
    }
    if (identity.utm_term) {
      localStorage.setItem('utm_term', identity.utm_term);
    }
    if (identity.utm_content) {
      localStorage.setItem('utm_content', identity.utm_content);
    }

    if (privateProSignUpData && privateProSignUpData.hkId !== null) {
      localStorage.setItem('hkId', privateProSignUpData.hkId?.toString());
      localStorage.setItem('proName', privateProSignUpData.proName);
      localStorage.setItem('proTeamName', privateProSignUpData.proTeamName);
    }
  }

  private addCredentials(identity, type, hkId = null): any {
    if (type === 'login' || type === 'signUpPassword') {
      const tidySessionCookie = this.cookieService.get('TidySession');

      return Object.assign({}, identity, {
        client_id: environment.client_id_login,
        client_secret: environment.client_secret_login,
        scope: 'customer',
        grant_type: 'password',
        track_data: {
          cookie: tidySessionCookie,
          referrer:            this.tidySession.firstUrlViewed?.referrer,
          first_url_viewed:    this.tidySession.firstUrlViewed?.value,
          first_url_viewed_at: this.tidySession.firstUrlViewed?.viewedAt,
          onboarding_flow: this.tidySession.onboardingFlow
        },
        homekeeper_id: hkId
      });
    } else if (type === 'signUp') {
      return Object.assign({}, identity, {
        client_id: environment.client_id_signup,
        client_secret: environment.client_secret_signup,
        scope: 'customerflow',
        grant_type: 'client_credentials',
        homekeeper_id: hkId
      });
    }
  }

  setAccountTypeStorage (account) {
    localStorage.removeItem('airbnb');
    localStorage.removeItem('company');
    if (account === 'airbnb' || account === 'rental' || account === 'realtor') {
      localStorage.setItem('airbnb', 'true');
    } else if (account === 'company') {
      localStorage.setItem('company', 'true');
    }
    this.events.publish('updateBackground');
  }

  getAccountType(account) {
    let typeAccount = account;
    if (account !== 'airbnb' && account !== 'company') {
      typeAccount = 'regular';
    }
    return typeAccount;
  }

  getAccountTypeOfFlow(account) {
    const typeAccount = this.getAccountType(account);
    this.setAccountTypeStorage(typeAccount);
    const data = {
      airbnb: {
        titleHeader: 'AirBnB / Rental Cleanings',
        accountType: 'rental'
      },
      regular: {
        titleHeader: 'Sign Up',
        accountType: 'regular'
      },
      company: {
        titleHeader: 'Sign Up',
        accountType: 'company'
      }
    }
    return data[typeAccount];
  }

  // Fire-and-forget: the customer never waits on this tracker, so a slow or failed post
  // must not reach Sentry. A login that runs again in the same page load (a re-entered
  // route, a double tap) posts once per customer and session.
  async setSession() {
    const customerId = parseInt(localStorage.getItem('customer_id'), 10);
    const sessionId = this.cookieService.get('TidySession');
    const sessionKey = `${customerId}:${sessionId}`;
    if (this.trackedSessionKey === sessionKey) {
      return {};
    }
    this.trackedSessionKey = sessionKey;
    const data = {
      record_type: 'customer',
      record_id: customerId,
      session_id: sessionId,
    };
    const url = 'https://us5bejpbfi.execute-api.us-west-2.amazonaws.com/dev/';
    const context = new HttpContext().set(REPORT_SLOW_REQUEST, false);
    try {
      return await firstValueFrom(this.http.post(url, data, { context }));
    } catch (_e) {
      return {};
    }
  }

  isAuthenticated(): boolean {
    if (typeof localStorage === 'undefined' || localStorage === null) {return false;}
    return localStorage.getItem('tidy_token') !== null && localStorage.getItem('tidy_token_type') !== null;
  }

  // Opens the login-as-client suppression window. Auto-clears after `maxMs` as a safety net so
  // an abandoned/failed swap can never leave 401-logout suppression stuck on. `maxMs` is set
  // comfortably above the per-request timeout (20s) so a slow-but-succeeding swap can't have the
  // net fire mid-flight and re-expose the very 401→logout→auth-modal bounce this prevents;
  // LoginPage calls endTokenLogin() on both success and failure, so this is only a backstop.
  beginTokenLogin(maxMs = 60000): void {
    this.tokenLoginInProgress = true;
    if (this.tokenLoginTimer) { clearTimeout(this.tokenLoginTimer); }
    this.tokenLoginTimer = setTimeout(() => {
      this.tokenLoginInProgress = false;
      this.tokenLoginTimer = null;
    }, maxMs);
  }

  // Closes the window, keeping it active for a short grace period so late-resolving 401s from
  // the outgoing session (in-flight when the swap began) still don't force a logout.
  endTokenLogin(graceMs = 2500): void {
    if (this.tokenLoginTimer) { clearTimeout(this.tokenLoginTimer); }
    this.tokenLoginTimer = setTimeout(() => {
      this.tokenLoginInProgress = false;
      this.tokenLoginTimer = null;
    }, graceMs);
  }

  isTokenLoginInProgress(): boolean {
    return this.tokenLoginInProgress;
  }

  createPreBooking() {
    localStorage.setItem('preBooking', 'true');
  }

  removePreBooking() {
    localStorage.removeItem('preBooking');
  }

  getPreBooking() {
    return (localStorage.getItem('preBooking') === 'true');
  }

  isPreBooking(page) {
    return this.getPreBooking() || page === 'SignUpPage';
  }

  async autoLogin(params) {
    const {addressId, authToken} = params;
    await this.login({ 'auth_token': authToken }, 'login')
    const address = await this.addresses.getAddress(addressId);
    await this.addresses.setCurrentAddress(address);
    await this.me.load();
    this.setSession();
  }

  recoverPassword(requestData) {
    return this.getTidyToken().then(response => {
      const res: any = response;

      const headers = new HttpHeaders({
        'Authorization': `bearer ${res.access_token}`,
        'Content-Type': 'application/json'
      });

      return new Promise((resolve, reject) => {
        this.http.post(
          `${this.apiUrl}api/v1/customers/recover-password`,
          JSON.stringify(requestData),
         { headers }
        )
          .subscribe(
          data => resolve(data),
          err => reject(err)
          );
      })
    });
  }

  changePassword(data) {
    return this.getTidyToken().then(response => {
      const res: any = response;

      const headers = new HttpHeaders({
        'Authorization': `bearer ${res.access_token}`,
        'Content-Type': 'application/json'
      });

      return this.http.post(`${this.apiUrl}api/v1/customers/change-password`,
        JSON.stringify(data), { headers })
        .toPromise();
    });
  }

  // Sends an SMS one-time login code to a phone number. The endpoint requires a
  // customerflow-scope token, so we mint one first (same pattern as recoverPassword).
  sendPhoneLoginCode(phone: string) {
    return this.getTidyToken().then(response => {
      const res: any = response;

      const headers = new HttpHeaders({
        'Authorization': `bearer ${res.access_token}`,
        'Content-Type': 'application/json'
      });

      return this.http.post(`${this.apiUrl}api/v1/customer/phone-login/send-code`,
        JSON.stringify({ phone }), { headers })
        .toPromise();
    });
  }

}
